import React, { useEffect, useMemo, useRef } from "react";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import StationSvg from "./StationSvg";
import KRobotSvg from "./KRobotSvg";
import ActiveZonesCanvas from "./ActiveZonesCanvas";
import PlannedPathSvg from "./PlannedPathSvg";
import CleaningImage from "../../interfaces/cleaning-map/cleaning-image";
import MapInfo from "../../interfaces/cleaning-map/map-info";
import RobotVersion, { getRobotWidthFromVersion } from "../../interfaces/cleaning-map/robot-version";
import { drawImageToCanvas, getMapCoordinatesFromPoint } from "../../utils/map";
import CoverageCanvas from "./CoverageCanvas";

const Map: React.FC<{
  cleaningImage: CleaningImage;
  dashboard?: boolean;
  disableAnimation?: boolean;
  robotVersion?: RobotVersion;
}> = ({ cleaningImage, dashboard = false, disableAnimation = false, robotVersion = RobotVersion.K900 }) => {
  const theme = useTheme();
  const matchesSmUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const obstacleCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const imageLoadingRef = useRef<{
    isObstacleMapLoaded: boolean;
    isCoverageMapLoaded: boolean;
    isActiveZonesLoaded: boolean;
  }>({ isActiveZonesLoaded: false, isCoverageMapLoaded: false, isObstacleMapLoaded: false });

  const obstacleMap = useMemo(() => cleaningImage.obstacleMap, [cleaningImage.obstacleMap]);
  const coverageMap = useMemo(() => cleaningImage.coverageMap, [cleaningImage.coverageMap]);
  const plannedPath = useMemo(() => cleaningImage.plannedPath, [cleaningImage.plannedPath]);
  const activeZones = useMemo(() => cleaningImage.activeRedZones, [cleaningImage.activeRedZones]);
  const robotPositionCoords = useMemo(() => cleaningImage.robotPosition, [cleaningImage.robotPosition]);
  const primaryStationPosition = useMemo(
    () => cleaningImage.primaryStationPosition,
    [cleaningImage.primaryStationPosition]
  );
  const secondaryStationPosition = useMemo(
    () => cleaningImage.secondaryStationPosition,
    [cleaningImage.secondaryStationPosition]
  );
  const robotRadius = useMemo(
    () => Math.round(getRobotWidthFromVersion(robotVersion) / ((obstacleMap?.resolution ?? 1) * 2)),
    [obstacleMap?.resolution, robotVersion]
  );

  const primaryMapInfo = useMemo(
    () =>
      ({
        width: cleaningImage.obstacleMap?.width ?? 0,
        height: cleaningImage.obstacleMap?.height ?? 0,
        resolution: cleaningImage.obstacleMap?.resolution ?? 1,
        map: "",
        originX: cleaningImage.obstacleMap?.originX ?? 0,
        originY: cleaningImage.obstacleMap?.originY ?? 0,
      } as MapInfo),
    [
      cleaningImage.obstacleMap?.height,
      cleaningImage.obstacleMap?.originX,
      cleaningImage.obstacleMap?.originY,
      cleaningImage.obstacleMap?.resolution,
      cleaningImage.obstacleMap?.width,
    ]
  );

  useEffect(() => {
    if (!obstacleMap || !obstacleCanvasRef.current) return;
    const canvas = obstacleCanvasRef.current;
    drawImageToCanvas(canvas, primaryMapInfo, obstacleMap, false);
    imageLoadingRef.current.isObstacleMapLoaded = true;
  }, [obstacleMap, primaryMapInfo]);

  const activeZonesLoadedHandler = () => {
    imageLoadingRef.current.isActiveZonesLoaded = true;
  };

  return (
    <Box sx={{ position: matchesSmUp ? "absolute" : "relative" }}>
      <Box
        sx={(theme) => ({
          backgroundColor: "rgb(242, 244, 247)",
          position: "relative",
          height: `calc(${cleaningImage.obstacleMap?.height}px + ${theme.spacing(4)})`,
          width: `calc(${cleaningImage.obstacleMap?.width}px + ${theme.spacing(4)})`,
        })}
      >
        <canvas
          style={{ position: "absolute", top: theme.spacing(2), left: theme.spacing(2) }}
          ref={obstacleCanvasRef}
        />
        <CoverageCanvas
          coverageMapInfo={coverageMap}
          primaryMapInfo={primaryMapInfo}
          robotPosition={robotPositionCoords}
          robotRadius={robotRadius}
          onCoverageMapLoad={() => (imageLoadingRef.current.isCoverageMapLoaded = true)}
          canvasStyle={{ position: "absolute", top: theme.spacing(2), left: theme.spacing(2) }}
        />
        <ActiveZonesCanvas
          activeZones={activeZones ?? null}
          primaryMapInfo={primaryMapInfo}
          onLoaded={activeZonesLoadedHandler}
        />
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${primaryMapInfo.width} ${primaryMapInfo.height}`}
          style={{ position: "absolute", padding: theme.spacing(2) }}
        >
          {plannedPath?.poses != null && (
            <React.Fragment>
              <PlannedPathSvg
                plannedPath={plannedPath}
                primaryMapInfo={primaryMapInfo} />
            </React.Fragment>
          )}
          {primaryStationPosition != null && primaryStationPosition.exists && (
            <StationSvg
              coordinateX={
                getMapCoordinatesFromPoint(
                  { x: primaryStationPosition.coordinateX, y: primaryStationPosition.coordinateY },
                  {
                    height: primaryMapInfo.height,
                    resolution: primaryMapInfo.resolution,
                    xPosition: primaryMapInfo.originX,
                    yPosition: primaryMapInfo.originY,
                  }
                ).x
              }
              coordinateY={
                getMapCoordinatesFromPoint(
                  { x: primaryStationPosition.coordinateX, y: primaryStationPosition.coordinateY },
                  {
                    height: primaryMapInfo.height,
                    resolution: primaryMapInfo.resolution,
                    xPosition: primaryMapInfo.originX,
                    yPosition: primaryMapInfo.originY,
                  }
                ).y
              }
              coordinateTheta={primaryStationPosition.coordinateTheta}
            />
          )}
          {secondaryStationPosition != null && secondaryStationPosition.exists && (
            <StationSvg
              coordinateX={
                getMapCoordinatesFromPoint(
                  { x: secondaryStationPosition.coordinateX, y: secondaryStationPosition.coordinateY },
                  {
                    height: primaryMapInfo.height,
                    resolution: primaryMapInfo.resolution,
                    xPosition: primaryMapInfo.originX,
                    yPosition: primaryMapInfo.originY,
                  }
                ).x
              }
              coordinateY={
                getMapCoordinatesFromPoint(
                  { x: secondaryStationPosition.coordinateX, y: secondaryStationPosition.coordinateY },
                  {
                    height: primaryMapInfo.height,
                    resolution: primaryMapInfo.resolution,
                    xPosition: primaryMapInfo.originX,
                    yPosition: primaryMapInfo.originY,
                  }
                ).y
              }
              coordinateTheta={secondaryStationPosition.coordinateTheta}
              isSecondaryStation
            />
          )}
          {robotPositionCoords != null && (
            <KRobotSvg
              coordinateX={
                getMapCoordinatesFromPoint(
                  { x: robotPositionCoords.x, y: robotPositionCoords.y },
                  {
                    height: primaryMapInfo.height,
                    resolution: primaryMapInfo.resolution,
                    xPosition: primaryMapInfo.originX,
                    yPosition: primaryMapInfo.originY,
                  }
                ).x
              }
              coordinateY={
                getMapCoordinatesFromPoint(
                  { x: robotPositionCoords.x, y: robotPositionCoords.y },
                  {
                    height: primaryMapInfo.height,
                    resolution: primaryMapInfo.resolution,
                    xPosition: primaryMapInfo.originX,
                    yPosition: primaryMapInfo.originY,
                  }
                ).y
              }
              coordinateTheta={robotPositionCoords.theta}
              disableAnimation={disableAnimation}
              robotRadius={robotRadius}
              model={robotVersion}
            />
          )}
        </svg>
        {dashboard &&
          (imageLoadingRef.current.isActiveZonesLoaded === false ||
            imageLoadingRef.current.isCoverageMapLoaded === false ||
            imageLoadingRef.current.isObstacleMapLoaded === false) && (
            <Box
              sx={{
                position: "absolute",
                top: "0",
                bottom: "0",
                left: "0",
                right: "0",
                backgroundColor: "rgb(242, 244, 247)",
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default Map;
